import * as React from "react";

import { DataTable, Page } from "src/components/common";
import { Select, Tooltip } from "antd";

import LocationAPI from "src/admin/Organization/Location/LocationAPI";

import PickListContext from "src/context/pickListContext";
import CourseAPI from "../Courses/CoursesAPI";
import useGetDataHook from "src/admin/Organization/Courses/useGetDataHook";
import Form from "antd/es/form";
import { Button } from "src/components/buttons";
import {
	filterByStatus,
	StatusFilterMethod,
	setStatusCellValue,
} from "../../../components/forms/StatusFilter";
import {
	ActionIcon,
	ClearFilterButton,
} from "src/components/forms/ActionColumnIconsAndButton";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import { AdminPermissions } from "src/common/Permissions";
import { toast } from "react-toastify";
interface ILocationWithCourseNames extends LocationDTO {
	courseNames: string;
}

interface IProps {
	history: any;
	actions: any;
	match: any;
	location?: any;
}

const OrganizationDatasetLocations = (props: IProps) => {
	const { organizationId, datasetId } = props.match.params;
	const [selectedLocation, setSelectedLocation] =
		React.useState<LocationDTO | null>(null);
	const [filtered, setFiltered] = React.useState<any>([]);
	const [tableIsLoading, setTableIsLoading] = React.useState<boolean>(false);

	const [{ data: locations, isLoading: isFetchingTableData }, setLocations] =
		useGetDataHook<LocationDTO[]>([], {
			apiCallFunc: LocationAPI.getDatasetLocations,
			params: [organizationId, datasetId],
		});

	const [{ data: organizationLocations }] = useGetDataHook<LocationDTO[]>([], {
		apiCallFunc: LocationAPI.getOrganizationActiveLocations,
		params: [{ organizationId }],
	});

	const [{ data: courses }] = useGetDataHook<CourseWithLocationNameDTO[]>([], {
		apiCallFunc: CourseAPI.getOrganizationCourses,
		params: [{ organizationId }],
	});

	const { pickListItems } = React.useContext(PickListContext);

	const getDescriptionFromPicklistContext =
		(prop: string) =>
		(rowInfo: any): string => {
			const picklistIdToFind = rowInfo.row[prop];
			if (!picklistIdToFind) {
				return "N/A";
			}
			const foundItem = pickListItems.find(
				(item: PickListItemResponseDTO) => item.id === picklistIdToFind
			);
			if (!foundItem || !foundItem.description) {
				return "N/A";
			} else {
				return foundItem.description;
			}
		};

	React.useEffect(() => {
		setFiltered([{ id: "isActive", value: "Active" }]);
	}, []);

	function onFilterChange(newFilter: any): void {
		setFiltered(newFilter);
	}

	const columns = [
		{
			Header: <Tooltip title="Location">Location</Tooltip>,
			accessor: "name",
		},
		{
			Header: <Tooltip title="Active">Active</Tooltip>,
			accessor: "isActive",
			Cell: setStatusCellValue,
			filterMethod: filterByStatus("isActive"),
			Filter: StatusFilterMethod,
		},
		{
			Header: <Tooltip title="Address">Address</Tooltip>,
			accessor: "address1",
		},
		{
			Header: <Tooltip title="City">City</Tooltip>,
			accessor: "city",
		},
		{
			Header: <Tooltip title="Province">Province</Tooltip>,
			accessor: "provinceId",
			Cell: getDescriptionFromPicklistContext("provinceId"),
		},
		{
			Header: <Tooltip title="Country">Country</Tooltip>,
			accessor: "countryId",
			Cell: getDescriptionFromPicklistContext("countryId"),
		},
		{
			Header: <Tooltip title="Courses">Courses</Tooltip>,
			accessor: "courseNames",
			Cell: (row: any) => <Tooltip title={row.value}>{row.value}</Tooltip>,
		},
		{
			Header: <Tooltip title="Actions">Actions</Tooltip>,
			accessor: "Actions",
			id: "Actions",
			Cell: (rowInfo: any) => (
				<AuthorizationWrapper
					type="disable"
					permissions={[AdminPermissions.EditOrganizationLocation]}
				>
					<ActionIcon
						onClick={deleteLocation(rowInfo)}
						title={`Remove Location: ${rowInfo.row.name} from Dataset`}
						type={"delete"}
					/>
				</AuthorizationWrapper>
			),
			Filter: (
				<ClearFilterButton
					onClick={() => setFiltered([{ id: "isActive", value: "Active" }])}
				/>
			),
		},
	];

	function deleteLocation(rowInfo: any) {
		const { id: locationId } = rowInfo.original;
		return async () => {
			setTableIsLoading(true);
			const res = await LocationAPI.removeLocationFromDataset({
				organizationId,
				datasetId,
				locationId,
			});
			if (res) {
				setLocations((prev: LocationDTO[]) =>
					prev.filter((loc: LocationDTO) => loc.id !== locationId)
				);
				toast.success("Location removed from dataset");
			} else {
				toast.error("Something went wrong");
			}
			setTableIsLoading(false);
		};
	}

	const tableData: ILocationWithCourseNames[] =
		locations.length > 0 && courses.length > 0
			? locations.map((loc) => {
					const filteredCourses = courses.filter(
						(c) => c.locationId === loc.id
					);
					return {
						...loc,
						courseNames: filteredCourses.map((c) => c.name).join(", "),
					};
			  })
			: [];

	const addLocationToDataset = async () => {
		if (selectedLocation && selectedLocation.name) {
			try {
				setTableIsLoading(true);
				const request: AddLocationToDatasetXrefLocationRequest = {
					organizationId,
					datasetId,
					locationId: selectedLocation.id,
				};
				await LocationAPI.addLocationToDataset(request);
				toast.success("Successfully added location to dataset");
				setLocations((prev: LocationDTO[]) => [...prev, selectedLocation]);
				setSelectedLocation(null);
			} catch (error) {
				toast.error("Something went wrong");
			}
			setTableIsLoading(false);
		}
	};

	const pickLocationToAdd = (e: any) => {
		const chosenLocation =
			organizationLocations.find((loc) => loc.name === e) || null;
		setSelectedLocation(chosenLocation);
	};

	// this array is used for filtering out already existing dataset locations
	const locationNames: string[] = locations.map((loc) => loc.name!);

	return (
		<Page title="Dataset Locations">
			<div className="container-fluid">
				<DataTable
					data={isFetchingTableData || tableIsLoading ? [] : tableData}
					columns={columns}
					filterable={true}
					hasActions={false}
					className="mb-4"
					filtered={filtered}
					onFilteredChange={onFilterChange}
					loading={isFetchingTableData || tableIsLoading}
				/>
				<div className="row">
					<div className="col-5 form-group">
						<AuthorizationWrapper
							type="hide"
							permissions={[AdminPermissions.EditOrganizationLocation]}
						>
							<label>Add Location to Dataset</label>
						</AuthorizationWrapper>
						<Form.Item>
							<AuthorizationWrapper
								type="hide"
								permissions={[AdminPermissions.EditOrganizationLocation]}
							>
								<Select
									value={selectedLocation ? selectedLocation.name : ""}
									onChange={pickLocationToAdd}
								>
									{organizationLocations
										.filter(
											(loc: LocationDTO) => !locationNames.includes(loc.name!)
										)
										.map((loc) => (
											<Select.Option key={`${loc.id}`} value={loc.name}>
												{loc.name}
											</Select.Option>
										))}
								</Select>
							</AuthorizationWrapper>
						</Form.Item>
					</div>
					<div className="col-1 form-group d-flex align-items-end">
						<AuthorizationWrapper
							type="hide"
							permissions={[AdminPermissions.EditOrganizationLocation]}
						>
							<Button
								className="btn-outline-primary"
								onClick={addLocationToDataset}
								disabled={!selectedLocation}
							>
								Add
							</Button>
						</AuthorizationWrapper>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default OrganizationDatasetLocations;
